import { Link, useNavigate } from 'react-router-dom';

import Jumbotron from '@/components/ui/box/Jumbotron';
import AbsenceLabel from '@/components/ui/labels/AbsenceLabel';
import Pagination from '@/components/ui/lists/Pagination';
import { useDateShorter } from '@/hooks/absences/useDateShorter';

import { type AbsencesIndexPayload } from '../AbsencesIndexPage';
import { contactTypeNames, reasonNames } from '../constants';
import { type ContactType, type Reason } from '../types';

import styles from './AbsencesIndexList.module.scss';

const AbsencesIndexList: React.FC<{
  organizationMembershipId: string;
  payload: AbsencesIndexPayload;
}> = ({ organizationMembershipId, payload: { absences, pagination } }) => {
  const navigate = useNavigate();
  const dateShorter = useDateShorter();
  const handleOnPageChanged = (page: number) => {
    navigate(`/absences/member/${organizationMembershipId}/?page=${page}`);
  };

  const { total_count, limit_value, current_page } = pagination;
  const pagesCount = Math.ceil(total_count / limit_value);

  return (
    <Jumbotron>
      <div className={styles.inner}>
        {absences.map(({ id: absenceId, date, latest_logs: latestLogs }) => (
          <div className={styles.date} key={absenceId}>
            <div className={styles.dateText}>{dateShorter(date)}</div>
            <ul className={styles.list}>
              {latestLogs.map(
                ({
                  id: latestLogId,
                  contact_type: contactType,
                  reason,
                  detail_comment: detailComment,
                  go_to_school_at: goToSchoolAt,
                  leave_school_at: leaveSchoolAt,
                  is_created_teacher: isCreatedTeacher,
                  confirmation,
                }) => (
                  <li className={styles.absenceLogListItem} key={latestLogId}>
                    <Link
                      className={styles.link}
                      to={`/absences/${absenceId}/type/${
                        contactType as string
                      }`}
                    >
                      <div className={styles.title}>
                        <AbsenceLabel
                          label={contactTypeNames[contactType as ContactType]}
                        />
                        <span className={styles.titleText}>
                          {reasonNames[reason as Reason]}
                        </span>
                        {isCreatedTeacher ? (
                          <span className={styles.titleLabel}>学校登録</span>
                        ) : (
                          confirmation?.confirmed && (
                            <span className={styles.confirmedLabel}>
                              先生確認済
                            </span>
                          )
                        )}
                      </div>
                      {goToSchoolAt && (
                        <div className={styles.time}>
                          登校時刻 {goToSchoolAt}
                        </div>
                      )}
                      {leaveSchoolAt && (
                        <div className={styles.time}>
                          下校時刻 {leaveSchoolAt}
                        </div>
                      )}
                      {detailComment && (
                        <div className={styles.description}>
                          {detailComment}
                        </div>
                      )}
                    </Link>
                  </li>
                ),
              )}
            </ul>
          </div>
        ))}
      </div>
      <Pagination
        initialPage={current_page}
        totalPage={pagesCount}
        onPageChanged={(page) => handleOnPageChanged(page)}
      />
    </Jumbotron>
  );
};

export default AbsencesIndexList;
